import { Component, OnDestroy, OnInit } from '@angular/core';
import { QuickAccessEntry, QuickAccessService } from '../../../shared/services/quick-access.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessengerService } from '../../../shared/services/messenger.service';
import { TxBdeAuftrag, TxBDEProtokoll } from '../../../shared/interfaces/bde';
import { TxApiService } from '../../../shared/services/txapi.service';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TranslateService } from '@ngx-translate/core';

/**
 * Component for managing Quick Access (Direktwahl) entries
 * Allows users to create, edit, and delete quick access buttons for job bookings
 * Uses inline editing similar to worksteps management
 */
@Component({
	selector: 'tx-quick-access',
	templateUrl: './tx-quick-access.component.html',
	styleUrls: ['./tx-quick-access.component.scss'],
})
export class TxQuickAccessComponent implements OnInit, OnDestroy {
	public quickAccessEntries$: Observable<QuickAccessEntry[]>;
	public quickAccessTitle$: Observable<string>;
	public auftragsArray: TxBdeAuftrag[] = [];
	public editingTitle = false;
	public editedTitle = '';

	// Edit mode tracking
	public EditMode: { [key: string]: boolean } = {};
	public Edited: { [key: string]: QuickAccessEntry } = {};

	// New entry fields
	public NewBezeichnung: string | null = null;
	public NewAuftragsnummer: string | null = null;
	public NewSchrittnummer: string | null = null;

	// Search controls for dropdowns
	public searchControlAuftragEdit: { [key: string]: FormControl } = {};
	public searchControlSchrittEdit: { [key: string]: FormControl } = {};
	public searchControlAuftragNew = new FormControl('');
	public searchControlSchrittNew = new FormControl('');

	// Filtered arrays
	public filteredAuftragsArrayNew: TxBdeAuftrag[] = [];
	public filteredSchritteArrayNew: any[] = [];
	public filteredAuftragsArrayEdit: { [key: string]: TxBdeAuftrag[] } = {};
	public filteredSchritteArrayEdit: { [key: string]: any[] } = {};

	private destroyed$ = new Subject<boolean>();
	private subscriptions: Subscription[] = [];

	constructor(
		private quickAccessService: QuickAccessService,
		private txApiService: TxApiService,
		private messenger: MessengerService,
		private route: ActivatedRoute,
		private translate: TranslateService
	) {
		this.quickAccessEntries$ = this.quickAccessService.getQuickAccessEntries();
		this.quickAccessTitle$ = this.quickAccessService.getQuickAccessTitle();
	}

	ngOnInit(): void {
		this.loadQuickAccessEntries();
		this.loadBDEData();
		
		// Initialize filtered arrays
		this.filteredAuftragsArrayNew = [...this.auftragsArray];
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
		this.subscriptions.forEach((sub) => sub.unsubscribe());
	}

	/**
	 * Load quick access entries from server
	 */
	loadQuickAccessEntries(): void {
		this.subscriptions.push(
			this.quickAccessService.loadQuickAccessEntries().subscribe(
				(entries) => {
					// Data is already updated in the service
				},
				(error) => {
					this.messenger.message(this.translate.instant('txQuickAccess.loadError'), 'error');
				}
			)
		);
	}

	/**
	 * Load BDE data (jobs and work steps) from server
	 */
	loadBDEData(): void {
		this.subscriptions.push(
			this.txApiService.callAPI('getBDE', {}).subscribe(
				(response) => {
					if (response.statuscode === 0 && Array.isArray(response.ListOfAuftrag)) {
						// Use TxBDEProtokoll to properly parse the response with nested ListOfAuftragsschritt
						const bdeProtokoll = new TxBDEProtokoll(
							response.statuscode,
							response.statustext,
							response.ListOfAuftrag
						);
						this.auftragsArray = bdeProtokoll.ListOfAuftrag;
						this.filteredAuftragsArrayNew = [...this.auftragsArray];
					} else {
						this.messenger.message(this.translate.instant('txQuickAccess.loadOrderDataError'), 'error');
					}
				},
				(error) => {
					this.messenger.message(this.translate.instant('txQuickAccess.loadOrderDataError'), 'error');
				}
			)
		);
	}

	/**
	 * Get edited version of entry (or create if not exists)
	 */
	getEdited(entry: QuickAccessEntry): QuickAccessEntry {
		if (!this.Edited[entry.id]) {
			this.Edited[entry.id] = { ...entry };
		}
		return this.Edited[entry.id];
	}

	/**
	 * Initialize search control for edit mode
	 */
	initSearchControlForEntry(entryId: string): void {
		if (!this.searchControlAuftragEdit[entryId]) {
			this.searchControlAuftragEdit[entryId] = new FormControl('');
			this.filteredAuftragsArrayEdit[entryId] = [...this.auftragsArray];
		}
		if (!this.searchControlSchrittEdit[entryId]) {
			this.searchControlSchrittEdit[entryId] = new FormControl('');
		}
	}

	/**
	 * Filter jobs for new entry
	 */
	filterAuftragsNew(searchTerm: string): void {
		const term = searchTerm.toLowerCase();
		this.filteredAuftragsArrayNew = this.auftragsArray.filter(
			(auftrag) =>
				auftrag.bezeichnung.toLowerCase().includes(term) ||
				auftrag.auftragsnummer.toLowerCase().includes(term)
		);
	}

	/**
	 * Filter jobs for edit mode
	 */
	filterAuftragsEdit(entryId: string, searchTerm: string): void {
		const term = searchTerm.toLowerCase();
		this.filteredAuftragsArrayEdit[entryId] = this.auftragsArray.filter(
			(auftrag) =>
				auftrag.bezeichnung.toLowerCase().includes(term) ||
				auftrag.auftragsnummer.toLowerCase().includes(term)
		);
	}

	/**
	 * Filter work steps for edit mode
	 */
	filterSchritteEdit(entryId: string, searchTerm: string, schritte: any[]): void {
		const term = searchTerm.toLowerCase();
		this.filteredSchritteArrayEdit[entryId] = schritte.filter((schritt) =>
			schritt.schrittbezeichnung.toLowerCase().includes(term)
		);
	}

	/**
	 * Filter work steps for new entry
	 */
	filterSchritteNew(searchTerm: string): void {
		const term = searchTerm.toLowerCase();
		const allSchritte = this.getAuftragsschritteForNew();
		this.filteredSchritteArrayNew = allSchritte.filter((schritt) =>
			schritt.schrittbezeichnung.toLowerCase().includes(term)
		);
	}

	/**
	 * Handle search input for new entry - Auftrag
	 */
	onSearchAuftragNew(event: KeyboardEvent): void {
		if (event.target instanceof HTMLInputElement) {
			this.filterAuftragsNew(event.target.value);
		}
	}

	/**
	 * Handle search input for new entry - Schritt
	 */
	onSearchSchrittNew(event: KeyboardEvent): void {
		if (event.target instanceof HTMLInputElement) {
			this.filterSchritteNew(event.target.value);
		}
	}

	/**
	 * Handle search input for edit mode - Auftrag
	 */
	onSearchAuftragEdit(entryId: string, event: KeyboardEvent): void {
		if (event.target instanceof HTMLInputElement) {
			this.filterAuftragsEdit(entryId, event.target.value);
		}
	}

	/**
	 * Handle search input for edit mode - Schritt
	 */
	onSearchSchrittEdit(entryId: string, event: KeyboardEvent, schritte: any[]): void {
		if (event.target instanceof HTMLInputElement) {
			this.filterSchritteEdit(entryId, event.target.value, schritte);
		}
	}

	/**
	 * Get filtered work steps for entry in edit mode
	 */
	getFilteredSchritteForEntry(entry: QuickAccessEntry): any[] {
		const allSchritte = this.getAuftragsschritteForEntry(entry);
		// Initialize if not exists
		if (!this.filteredSchritteArrayEdit[entry.id] || this.filteredSchritteArrayEdit[entry.id].length === 0) {
			this.filteredSchritteArrayEdit[entry.id] = allSchritte;
		}
		return this.filteredSchritteArrayEdit[entry.id];
	}

	/**
	 * Check if we can add a new entry
	 */
	canAdd(entries: QuickAccessEntry[]): boolean {
		return (
			!!this.NewBezeichnung &&
			!!this.NewAuftragsnummer &&
			!!this.NewSchrittnummer &&
			!entries.find((e) => e.bezeichnung === this.NewBezeichnung)
		);
	}

	/**
	 * Get job by job number
	 */
	getAuftragByNumber(auftragsnummer: string): TxBdeAuftrag | null {
		return this.auftragsArray.find((a) => a.auftragsnummer === auftragsnummer) || null;
	}

	/**
	 * Get work steps for selected job (for editing)
	 */
	getAuftragsschritteForEntry(entry: QuickAccessEntry) {
		const auftrag = this.getAuftragByNumber(this.getEdited(entry).auftragsnummer);
		return auftrag?.ListOfAuftragsschritt || [];
	}

	/**
	 * Get work steps for new entry
	 */
	getAuftragsschritteForNew() {
		if (!this.NewAuftragsnummer) return [];
		const auftrag = this.getAuftragByNumber(this.NewAuftragsnummer);
		return auftrag?.ListOfAuftragsschritt || [];
	}

	/**
	 * Get filtered work steps for new entry
	 */
	getFilteredSchritteForNew(): any[] {
		const allSchritte = this.getAuftragsschritteForNew();
		// Initialize if not exists or empty
		if (!this.filteredSchritteArrayNew || this.filteredSchritteArrayNew.length === 0) {
			this.filteredSchritteArrayNew = allSchritte;
		}
		return this.filteredSchritteArrayNew;
	}

	/**
	 * Handle job selection change for editing
	 */
	onAuftragChange(entry: QuickAccessEntry): void {
		const edited = this.getEdited(entry);
		const auftrag = this.getAuftragByNumber(edited.auftragsnummer);
		if (auftrag) {
			edited.auftragsbezeichnung = auftrag.bezeichnung;
			// Reset work step when job changes
			edited.schrittnummer = '';
			edited.schrittbezeichnung = '';
			// Update filtered work steps
			this.filteredSchritteArrayEdit[entry.id] = auftrag.ListOfAuftragsschritt || [];
		}
		// Reset search
		if (this.searchControlAuftragEdit[entry.id]) {
			this.searchControlAuftragEdit[entry.id].setValue('');
		}
		this.filteredAuftragsArrayEdit[entry.id] = [...this.auftragsArray];
	}

	/**
	 * Handle work step selection change for editing
	 */
	onSchrittChange(entry: QuickAccessEntry): void {
		const edited = this.getEdited(entry);
		const auftrag = this.getAuftragByNumber(edited.auftragsnummer);
		const schritt = auftrag?.ListOfAuftragsschritt.find((s) => s.schrittnummer === edited.schrittnummer);
		if (schritt) {
			edited.schrittbezeichnung = schritt.schrittbezeichnung;
		}
		// Reset search
		if (this.searchControlSchrittEdit[entry.id]) {
			this.searchControlSchrittEdit[entry.id].setValue('');
		}
	}

	/**
	 * Handle job selection change for new entry
	 */
	onNewAuftragChange(): void {
		const auftrag = this.getAuftragByNumber(this.NewAuftragsnummer);
		// Reset work step when job changes
		this.NewSchrittnummer = null;
		// Reset search and initialize filtered arrays
		this.searchControlAuftragNew.setValue('');
		this.searchControlSchrittNew.setValue('');
		this.filteredAuftragsArrayNew = [...this.auftragsArray];
		this.filteredSchritteArrayNew = this.getAuftragsschritteForNew();
	}

	/**
	 * Add a new quick access entry
	 */
	onAddEntry(entries: QuickAccessEntry[]): void {
		if (!this.canAdd(entries)) return;

		const auftrag = this.getAuftragByNumber(this.NewAuftragsnummer);
		const schritt = auftrag?.ListOfAuftragsschritt.find((s) => s.schrittnummer === this.NewSchrittnummer);

		if (!auftrag || !schritt) {
			this.messenger.message(this.translate.instant('txQuickAccess.selectValidOrderTask'), 'error');
			return;
		}

		const newEntry: QuickAccessEntry = {
			id: this.quickAccessService.generateId(),
			bezeichnung: this.NewBezeichnung,
			auftragsnummer: auftrag.auftragsnummer,
			auftragsbezeichnung: auftrag.bezeichnung,
			schrittnummer: schritt.schrittnummer,
			schrittbezeichnung: schritt.schrittbezeichnung,
		};

		this.subscriptions.push(
			this.quickAccessService.addQuickAccessEntry(newEntry).subscribe(
				(response) => {
					if (response.statuscode === 0) {
						this.messenger.message(this.translate.instant('txQuickAccess.createSuccess'), 'success');
						// Reset form
						this.NewBezeichnung = null;
						this.NewAuftragsnummer = null;
						this.NewSchrittnummer = null;
					} else {
						this.messenger.message(this.translate.instant('txQuickAccess.createError'), 'error');
					}
				},
				(error) => {
					this.messenger.message(this.translate.instant('txQuickAccess.createError'), 'error');
				}
			)
		);
	}

	/**
	 * Save edited entry
	 */
	onSave(entry: QuickAccessEntry): void {
		const edited = this.getEdited(entry);

		// Validate
		if (!edited.bezeichnung || !edited.auftragsnummer || !edited.schrittnummer) {
			this.messenger.message(this.translate.instant('txQuickAccess.fillAllFields'), 'error');
			return;
		}

		this.subscriptions.push(
			this.quickAccessService.updateQuickAccessEntry(entry.id, edited).subscribe(
				(response) => {
					if (response.statuscode === 0) {
						this.messenger.message(this.translate.instant('txQuickAccess.updateSuccess'), 'success');
						this.EditMode[entry.id] = false;
						delete this.Edited[entry.id];
					} else {
						this.messenger.message(this.translate.instant('txQuickAccess.updateError'), 'error');
					}
				},
				(error) => {
					this.messenger.message(this.translate.instant('txQuickAccess.updateError'), 'error');
				}
			)
		);
	}

	/**
	 * Discard changes to entry
	 */
	onDiscard(entry: QuickAccessEntry): void {
		delete this.Edited[entry.id];
		this.EditMode[entry.id] = false;
	}

	/**
	 * Delete a quick access entry
	 */
	deleteEntry(entry: QuickAccessEntry): void {
		if (!confirm(this.translate.instant('txQuickAccess.deleteConfirm', { name: entry.bezeichnung }))) {
			return;
		}

		this.subscriptions.push(
			this.quickAccessService.deleteQuickAccessEntry(entry.id).subscribe(
				(response) => {
					if (response.statuscode === 0) {
						this.messenger.message(this.translate.instant('txQuickAccess.deleteSuccess'), 'success');
					} else {
						this.messenger.message(this.translate.instant('txQuickAccess.deleteError'), 'error');
					}
				},
				(error) => {
					this.messenger.message(this.translate.instant('txQuickAccess.deleteError'), 'error');
				}
			)
		);
	}

	/**
	 * Handle drag and drop reordering of entries
	 */
	onDrop(event: CdkDragDrop<QuickAccessEntry[]>, entries: QuickAccessEntry[]): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const reorderedEntries = [...entries];
		moveItemInArray(reorderedEntries, event.previousIndex, event.currentIndex);

		this.subscriptions.push(
			this.quickAccessService.saveQuickAccessEntries(reorderedEntries).subscribe(
				(response) => {
					if (response.statuscode === 0) {
						this.messenger.message(this.translate.instant('txQuickAccess.orderSuccess'), 'success');
					} else {
						this.messenger.message(this.translate.instant('txQuickAccess.orderError'), 'error');
					}
				},
				(error) => {
					this.messenger.message(this.translate.instant('txQuickAccess.orderError'), 'error');
				}
			)
		);
	}

	/**
	 * Start editing the title
	 */
	startEditingTitle(currentTitle: string): void {
		this.editedTitle = currentTitle;
		this.editingTitle = true;
	}

	/**
	 * Save the edited title
	 */
	saveTitle(): void {
		if (!this.editedTitle || this.editedTitle.trim() === '') {
			this.messenger.message(this.translate.instant('txQuickAccess.enterLabel'), 'error');
			return;
		}

		this.subscriptions.push(
			this.quickAccessService.updateQuickAccessTitle(this.editedTitle.trim()).subscribe(
				(response) => {
					if (response.statuscode === 0) {
						this.messenger.message(this.translate.instant('txQuickAccess.labelUpdateSuccess'), 'success');
						this.editingTitle = false;
					} else {
						this.messenger.message(this.translate.instant('txQuickAccess.labelUpdateError'), 'error');
					}
				},
				(error) => {
					this.messenger.message(this.translate.instant('txQuickAccess.labelUpdateError'), 'error');
				}
			)
		);
	}

	/**
	 * Cancel editing the title
	 */
	cancelEditingTitle(): void {
		this.editingTitle = false;
		this.editedTitle = '';
	}
}
